import { Label, Root, Series, Tooltip } from "@amcharts/amcharts5";
import { forIn } from "lodash";

import { ChartFamily, ChartName, ChartSettings } from "@/classes/BaseChart/index";
import { STATS_METRICS } from "@/helpers/charts";
import { getChartAllSettings } from "@/settings/charts";
import { InputDictionary } from "@/types/common";

class ChartWithTooltip {
  declare settings: ChartSettings;
  declare root: Root;
  declare chartName: ChartName;
  declare chartFamily: ChartFamily;
  declare inputsDictionary: Record<string, InputDictionary[]>;

  protected createTooltip(series: Series, createParams: (paramsTooltip: Label) => Record<string, unknown>[]): void {
    const tooltip = series.set("tooltip", Tooltip.new(this.root, {}));

    tooltip.label.set("lineHeight", 1.4);
    tooltip.label.set("textBaseline", "bottom");
    tooltip.label.adapters.add("text", (text, target) => {
      const params = createParams(target);
      const allSettings = getChartAllSettings(this.chartName, this.chartFamily);

      let chartSettings: Record<string, unknown>[] = [];
      let textTooltip = "";

      forIn(allSettings, (setting, key) => {
        if (setting.tooltipOrder !== undefined) {
          if (setting.isStatistic) {
            STATS_METRICS.forEach((statElem) => {
              chartSettings.push({
                key,
                value: `${this.settings[key]}_${statElem}`,
                dictionary: setting.dictionary,
                tooltipOrder: setting.tooltipOrder,
              });
            });
          } else {
            chartSettings.push({
              key,
              value: this.settings[key],
              dictionary: setting.dictionary,
              tooltipOrder: setting.tooltipOrder,
            });
          }
        }
      });

      chartSettings = chartSettings.sort((a, b) => {
        return (a.tooltipOrder as number) - (b.tooltipOrder as number);
      });

      const tooltipValues = target.dataItem?.dataContext as Record<string, unknown>;

      if (tooltipValues) {
        const chartSettingsFiltered = chartSettings.filter((el: Record<string, unknown>) =>
          params.find((label) => {
            return el.value === label.key;
          })
        );

        chartSettingsFiltered.forEach((chartSetting, index) => {
          const dictionary = this.inputsDictionary;
          const dictionaryList = dictionary[chartSetting.dictionary as string];

          const metricDictionary = dictionaryList?.find((metric: InputDictionary) => {
            return metric.value === chartSetting.value;
          });

          if (metricDictionary && metricDictionary.formatter) {
            const { title, formatter } = metricDictionary;

            const useFormat = (val: string | number | null) => {
              const config: Record<string, string | number> = {};
              // TODO: Think what is a good way to calulate tooltip accuracy.
              //
              // import { calculateTooltipValuesAccuracy } from "@/helpers/charts";
              // import { MAX_FRACTION_DIGITS_ACCURACY, MIN_FRACTION_DIGITS_ACCURACY } from "@/helpers/constants";
              //
              // const tooltipsAccuracy = calculateTooltipValuesAccuracy(tooltipValues);
              //
              // if (
              //   tooltipsAccuracy &&
              //   tooltipsAccuracy >= MIN_FRACTION_DIGITS_ACCURACY &&
              //   tooltipsAccuracy <= MAX_FRACTION_DIGITS_ACCURACY
              // ) {
              //   config.maximumFractionDigits = tooltipsAccuracy;
              // }

              if (this.settings.compactFormat) {
                config.notation = "compact";
              }

              return formatter(val, config);
            };

            const value = params.find((el: Record<string, unknown>) => {
              return chartSetting.value === el.key;
            })?.value as string | number | null;

            textTooltip += `[bold]${title}:[/] ${useFormat(value)}${
              index === chartSettingsFiltered.length - 1 ? "" : "\n"
            }`;
          } else {
            const value = params.find((el: Record<string, unknown>) => chartSetting.value === el.key)?.value;

            textTooltip += `[bold]${chartSetting.value}:[/] ${value}${
              index === chartSettingsFiltered.length - 1 ? "" : "\n"
            }`;
          }
        });
      }

      return textTooltip;
    });
  }
}

export default ChartWithTooltip;
